// Percorso file: src/pages/home/HomePlayer.jsx
//
// Home del ruolo PLAYER — ricostruita (cantiere HOME-PLAYER).
//
// La pagina ha tre blocchi: l'intestazione (titolo + squadra), la riga
// dei canali del club e le voci. Il telaio — barra utente, banda con
// «Switch role», colonna §4 — lo porta HomeShell: la pagina non lo
// ridisegna. È una home di PRIMO LIVELLO, quindi nessun «Back» nella
// banda (doc 07 §7.2): `backTo` non si passa, esattamente come in
// HomeParent.jsx.
//
// ⚠️ NIENTE DATI DEL GIOCATORE. Nessuna voce è più «coming soon»
// (l'ultima spenta, Game Call Ups, è stata tolta in R8.2), e questo è un
// fatto architettonico, non una pigrizia: la pagina NON chiama né le
// presenze né gli eventi, quindi non c'è nessun badge di conteggio e
// nessun riquadro Club Legacy (esclusi per decisione del committente:
// non si faranno). Di conseguenza
// NON si legge `user.uid` da nessuna parte: era il campo che il vecchio
// file demolito in `2462842` cercava e che sui documenti nuovi non
// esiste (ricognizione HOME-PLAYER §4), ed è morto insieme a lui. Se mai
// una voce dovrà leggere dati del giocatore, l'identificativo da usare è
// `user.id` oppure `user.id || user.firebaseUid` (la forma delle
// presenze), MAI `user.uid`.
//
// ⚠️ «CALENDAR — EVENTS» È TORNATA ACCESA (cantiere CALENDAR, fetta E) e
// non intacca nulla di quanto sopra: naviga a /my-calendar e basta: nessun
// dato del giocatore si legge QUI. È la pagina di destinazione, dietro
// <PrivateRoute> (App.jsx), a mostrargli la propria settimana invece del
// calendario di tutto il club, e le squadre se le ricava da sé (fetta C).
//
// ⚠️ «LIVE CHAT» È TORNATA ACCESA (cantiere LIVE CHAT), e nemmeno lei
// intacca quanto sopra: naviga a /messaging e basta. È la pagina
// Messaging, dietro <PrivateRoute> (App.jsx), a leggere ciò che le serve.
//   ⚠️ SEQUENZA DEL TRAVASO IN PRODUZIONE: questa accensione è per il
//   COLLAUDO IN BETA (dati finti). In produzione il bottone va acceso
//   INSIEME al deploy delle regole di sicurezza della chat (il recinto),
//   MAI PRIMA: un socio non deve poter entrare in una chat il cui recinto
//   non è ancora attivo.
//
// ⚠️ LA SQUADRA DEL GIOCATORE STA NELLA SUA TESSERA, non sul suo
// documento. Questa pagina leggeva il campo di PERSONA `user.team`, che
// Team Assignment non scrive più: la squadra si assegna sulla TESSERA, e
// da lì la riga restava muta a giocatori che una squadra ce l'hanno
// eccome. Ora si legge dalla coppia canonica `{teams, allTeams}` della
// tessera dell'anno OPERATIVO, come fanno View Profile
// (MemberProfileModal.jsx) e la home del genitore (HomeParent.jsx).
// I `contexts` li porta già `useAuth()`: nessuna chiamata in più.
//
// L'UNICA chiamata al backend della pagina resta `GET /teams`, e serve a
// una cosa sola: la CATEGORIA della pastiglia (§2). La categoria non
// sta sull'utente — sta sulla SQUADRA — e la tessera porta il NOME
// della squadra, non il suo id: senza quella lettura la pastiglia non
// avrebbe nulla da dire. È la stessa lettura, con la stessa logica di
// ripiego, che fa HomeParent.jsx: aperta a ogni utente autenticato, e
// se fallisce non si avvisa e non si riprova — la pastiglia tace e il
// nome della squadra, che è il dato che conta, resta a schermo.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaComments,
  FaCalendarAlt,
  FaClipboardCheck,
  FaFutbol,
  FaShoppingBag,
  FaUserCircle,
  FaLifeRing
} from "react-icons/fa";
import HomeShell from "../../components/HomeShell";
import AuthAvatar from "../../components/AuthAvatar";
import { PastigliaCategoriaSeNota } from "../../components/PastigliaCategoria";
import { SOCIAL, SHOP_URL } from "../../components/clubLinks";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../api/api";
import { normalizza } from "../../utils/roleUtils";
// LA SEDE UNICA DELLA LETTURA DI SQUADRA (cantiere «lettore unico»): qui
// arriva, gia' scritta, l'unione che questa pagina teneva in copia locale.
// `valeSuTutteLeSquadre` non si importa piu': la legge il modulo, dentro il
// ripiego di `squadreDellaTessera`.
import { squadreDelSoggetto } from "../../utils/squadre";
import EnrolNextSeason from "./EnrolNextSeason";
import AustraliaGoalButton from "./AustraliaGoalButton";
import "./HomePlayer.css";

/* ===============================
   LA SQUADRA DEL GIOCATORE — le copie di pagina NON CI SONO PIU'

   Qui vivevano `annoDiLista`, `squadreDellaTessera`, `tesseraValida` e
   `squadreDelSoggetto`: copie dichiarate delle gemelle di
   `MemberProfileModal.jsx` e `HomeParent.jsx`, in attesa — parole del
   commento che stava qui — del «modulo condiviso, materia del cantiere
   lettore unico». Quel modulo ora esiste, e sono due: `utils/stagione.js`
   per l'anno, `utils/squadre.js` per la tessera e le sue squadre. I corpi
   sono quelli di qui, invariati: non e' stata riscritta una riga, sono
   state tolte quattro copie.

   `squadreDelSoggetto(documento, anno)` prende ora i `contexts` invece del
   documento intero — `squadreDelSoggetto(user?.contexts, anno)` — con le
   stesse opzioni predefinite: solo tessere ACTIVE, tessera senz'anno
   esclusa, NESSUNA tessera amministrativa.
=============================== */

const HomePlayer = () => {
  // ⚠️ `operatingSeason` viene dalla stessa cassettina di `user`: l'ANNO
  // OPERATIVO del club, gia' normalizzato a intero da AuthContext. Lo
  // prendono di qui, con la stessa riga, UsersList, TeamAssignment,
  // MemberProfileModal e HomeParent: nessuna chiamata propria a
  // `GET /season-config`, e un solo posto che dice l'annata. Fuori da
  // `seasonStatus: "ready"` vale `null`, e il predicato dell'anno lo tratta
  // come «annata ignota».
  const { user, operatingSeason } = useAuth();
  const navigate = useNavigate();

  // L'unico nullo che il predicato dell'anno conosce.
  const annoOperativo = operatingSeason == null ? null : operatingSeason;

  // LA SQUADRA, DALLA TESSERA DELL'ANNO OPERATIVO — non più dal campo di
  // persona `user.team`, che Team Assignment non scrive.
  // Un giocatore ha, di norma, una tessera sola: il nome della squadra è il
  // PRIMO dell'unione. «Tutte le squadre» non è un nome e non se ne inventa
  // uno: si dice a schermo con la stessa parola della scheda («All teams»),
  // e la pastiglia tace perché non c'è nessuna squadra da cercare.
  const { nomi, tutte } = squadreDelSoggetto(user?.contexts, annoOperativo);
  const squadra = tutte ? "" : nomi[0] || "";

  // Il nome per la FOTO qui sopra: non si stampa a schermo (il nome sta
  // già nella barra utente del telaio), serve solo ad AuthAvatar per le
  // INIZIALI di ripiego quando la foto non c'è. Stessa composizione di
  // UserBar.jsx: `filter(Boolean)` perché un utente ridotto può non avere
  // né nome né cognome, e in quel caso AuthAvatar mostra il suo «?».
  const nome = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim();

  // Le squadre, come arrivano da GET /teams — i documenti INTERI, non una
  // proiezione. Servono a due cose, entrambe qui sotto: la CATEGORIA della
  // pastiglia e il LINK Australia Goal della squadra.
  const [squadre, setSquadre] = useState([]);

  // ⚠️ NON si legge se `squadra` è vuota: senza squadra non c'è pastiglia
  // da riempire, e la chiamata sarebbe sprecata. La dipendenza è la
  // STRINGA `squadra`, non l'oggetto `user`: `refreshUserFromBackend`
  // restituisce un oggetto NUOVO a ogni chiamata (avvertenza di
  // HomeIncomplete.jsx, ripresa in HomeParent.jsx), e un
  // effetto che dipendesse da `user` rileggerebbe le squadre a ogni giro.
  useEffect(() => {
    if (!squadra) return undefined;

    let annullato = false;

    api
      .get("/teams")
      .then((dati) => {
        if (!annullato) setSquadre(Array.isArray(dati) ? dati : []);
      })
      .catch(() => {
        /* nessuna categoria: la pastiglia tace */
      });

    return () => {
      annullato = true;
    };
  }, [squadra]);

  // CATEGORIA E LINK della squadra, dallo STESSO documento e con lo stesso
  // `find` (doc 07 §2). Stessa logica di `categoriaDi` in HomeParent.jsx e
  // in TeamAssignment.jsx: si cerca per NOME, perché la tessera contiene il
  // nome della squadra e non il suo id.
  // ⚠️ La pastiglia porta la CATEGORIA e nient'altro: divisione e
  // campionato NON ci entrano (§2 «Confini»).
  // ⚠️ IL LINK ESCE DA QUI E NON DA UNA SECONDA RICERCA (cantiere
  // TEAM-LINK): la voce di squadra era già isolata, e cercarla due volte
  // avrebbe messo due `find` a rischio di divergere sul metro del nome.
  // Cambia ciò che il `useMemo` RESTITUISCE — un oggetto invece di una
  // stringa — non ciò che legge. `String(...).trim()` è il metro che la
  // pagina del coach usa già sui nomi di squadra: un campo assente, non
  // testuale o di soli spazi diventa stringa vuota, ed è la stringa vuota
  // che fa tacere il bottone.
  const { categoria, link } = useMemo(() => {
    const voce = squadre.find(
      (t) => t && typeof t === "object" && t.name === squadra
    );
    return voce
      ? {
          categoria: normalizza(voce.category),
          link: String(voce.link || "").trim()
        }
      : { categoria: "", link: "" };
  }, [squadre, squadra]);

  return (
    <HomeShell>
      {/* LA FOTO DEL GIOCATORE, tonda e centrata SOPRA il titolo (rev.
          utente): il titolo resta centrato com'era (§3.1) e la foto non
          gli sta di fianco, gli sta sopra. Il respiro fra le due è quello
          normale del §4, 16px, e non è dichiarato qui: lo porta già il
          `gap` di `.qpsc-column` (HomeShell.css), perché il gruppo
          della foto è un blocco della colonna come gli altri. Aggiungere
          un margine lo raddoppierebbe a 32.

          È il componente ESISTENTE AuthAvatar, con le stesse prop con cui
          lo usano HomeParent.jsx e UsersList.jsx: la foto sullo
          Storage è chiusa in lettura e va scaricata con fetch autenticata,
          cosa che un <img> non sa fare. Senza `photoKey` il componente non
          chiama nemmeno il backend e va DIRITTO al ripiego, che è quello
          NORMALE — le INIZIALI: nessun `fallback` e nessun `fallbackSrc`,
          così il ripiego resta quello del componente e non si disabilita.
          ⚠️ L'identificativo è `user.id`, MAI `user.uid` (avvertenza in
          testa al file): `uid` sui documenti nuovi non esiste. */}
      <div className="qpsc-player-avatar">
        <AuthAvatar
          userId={user?.id}
          photoKey={user?.photoUrl || ""}
          name={nome}
          alt={nome || "Player"}
          className="qpsc-player-avatar__photo"
        />
      </div>

      <h1 className="qpsc-page-title">QPSC Player</h1>

      {/* §2 SQUADRA e pastiglia sono UN gruppo orizzontale: la pastiglia
          sta sulla stessa riga della squadra, alla sua destra, centrata
          in verticale. La pastiglia TACE se la categoria non è nota
          (PastigliaCategoriaSeNota): un giocatore la cui squadra non ha
          categoria non esibisce un timbro «NO CATEGORY».
          LA RIGA C'È SEMPRE, anche senza squadra. Prima spariva, perché il
          campo di persona `team` nasceva vuoto e restare muti era l'unica
          cosa onesta da fare; ora la squadra viene dalla TESSERA, e il
          silenzio non si distinguerebbe da un guasto. Si scrive «No team
          yet» — la stessa parola della home del genitore — e non
          «Pending», che nel modello nuovo è uno STATO e vuol dire un'altra
          cosa. Con «All teams» e con «No team yet» la categoria non è nota
          per definizione, quindi la pastiglia resta muta. */}
      <p className="qpsc-player-teamline">
        <span className="qpsc-player-team">
          {tutte ? "All teams" : squadra || "No team yet"}
        </span>
        <PastigliaCategoriaSeNota category={categoria} />
      </p>

      {/* §7.3 i quattro canali del club: glifi oro nudi, su una riga
          centrata. Stesso markup e stessi indirizzi di HomeParent.jsx e
          della home del SuperAdmin — gli indirizzi arrivano dalla sede
          unica, components/clubLinks.js, e non si ricopiano. */}
      <div className="qpsc-social">
        {SOCIAL.map(({ href, title, Icon }) => (
          <a
            key={title}
            className="qpsc-social__link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={title}
            aria-label={title}
          >
            <Icon />
          </a>
        ))}
      </div>

      {/* LE VOCI, in DUE <nav>: fra il primo (Live Chat) e il secondo si
          incunea il rinnovo (v. qui sotto), come nella home del genitore
          si incunea la colonna dei figli. Il respiro fra le voci è quello normale del
          §4, 16px, che porta già `.qpsc-menu` — con DUE eccezioni volute
          dall'utente: la prima voce (Live Chat) e My Profile (che apre
          la coda, con «More / Assistance» sotto al gap normale)
          si staccano dalle quattro centrali con un respiro DOPPIO, 32px.
          Il doppio non è un numero nuovo: è lo stesso scostamento che fa
          la home del genitore (`.qpsc-parent-menu`, HomeParent.css),
          16px di margine che si SOMMANO ai 16 di gap. Lo porta la classe
          `.qpsc-player-spaced`, agganciata alla voce che APRE il gruppo
          successivo — Calendar e My Profile — esattamente come fa
          HomeParent, che il margine lo mette sempre in testa al blocco
          che si stacca, mai in coda a quello che lo precede.
          Le icone sono Font Awesome su TUTTE le voci, mai emoji: regola
          tutto-o-niente del §5 (il 🛍️ del file storico è sostituito da
          FaShoppingBag). Il separatore delle voci composte è il trattino
          lungo con spazi (§3.3). */}
      <nav className="qpsc-menu">
        {/* LIVE CHAT, ACCESA (cantiere LIVE CHAT): naviga a /messaging, la
            rotta della pagina Messaging, ora ricostruita e dietro
            <PrivateRoute> (App.jsx). La forma è quella delle altre voci
            vive della pagina — «Calendar — Events» e «My Profile» qui
            sotto: `<button>` con `onClick` e `navigate`, mai un `<Link>` —
            e l'etichetta perde la coda « — coming soon ».
            ⚠️ SEQUENZA DEL TRAVASO IN PRODUZIONE: questa accensione è per
            il COLLAUDO IN BETA (dati finti). In produzione il bottone va
            acceso INSIEME al deploy delle regole di sicurezza della chat
            (il recinto), MAI PRIMA: un socio non deve poter entrare in una
            chat il cui recinto non è ancora attivo. */}
        <button
          type="button"
          className="qpsc-btn-gold"
          onClick={() => navigate("/messaging")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaComments />
          </span>
          Live Chat
        </button>
      </nav>

      {/* IL RINNOVO (YS-11.2), sotto la Live Chat. Si mostra da sé solo a
          stagioni sovrapposte e a iscrizioni aperte — la condizione sta tutta
          dentro il componente, che legge la cassettina della stagione da
          `useAuth()`: qui non c'è nulla da decidere.

          ⚠️ IL `<nav>` SI CHIUDE QUI E SE NE APRE UN SECONDO, ed è il motivo
          per cui questa è l'unica riga di struttura che la fetta tocca: il
          rinnovo NON è navigazione — apre un pannello e scrive — e dentro un
          `<nav>` non ci può stare. È lo stesso schema di HomeParent, che di
          `<nav>` ne ha due proprio perché un blocco non-navigazione (i figli)
          si incunea fra le voci.

          IL RITMO NON CAMBIA DI UN PIXEL, né col pulsante né senza. La
          colonna della home ha lo stesso `gap` di 16px del `.qpsc-menu`
          (HomeShell.css), quindi: con il pulsante Live Chat → 16 → Enrol →
          32 → Calendar (il doppio di `.qpsc-player-spaced` continua a isolare
          il gruppo di testa); senza il pulsante il componente rende `null`,
          non entra nel flusso, e restano i 16 del gap più i 16 del margine =
          i 32 di prima. */}
      <EnrolNextSeason />

      <nav className="qpsc-menu">
        {/* Prima voce del gruppo centrale: qui cade il doppio respiro che
            isola Live Chat in cima. La classe non cambia — il ritmo della
            colonna resta quello di prima.
            CALENDAR — EVENTS, RIACCESA (cantiere CALENDAR, fetta E):
            naviga a /my-calendar, la rotta del calendario del socio
            (App.jsx, dietro <PrivateRoute>), dove il giocatore vede la
            propria settimana, non il calendario di tutto il club. La forma è
            quella delle altre voci vive della pagina — «My Profile» qui
            sotto: `<button>` con `onClick` e `navigate`, mai un `<Link>` —
            e l'etichetta perde la coda « — coming soon ». */}
        <button
          type="button"
          className="qpsc-btn-gold qpsc-player-spaced"
          onClick={() => navigate("/my-calendar")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaCalendarAlt />
          </span>
          Calendar &mdash; Events
        </button>

        {/* MY ATTENDANCE, RIACCESA (cantiere ATTENDANCE, fetta F2): naviga
            a /attendance, la FORMA NUDA della rotta (App.jsx, dietro
            <PrivateRoute>), dove il giocatore trova gli eventi della SUA
            squadra e risponde «ci sarò / non ci sarò». La squadra se la
            ricava la PAGINA dalle tessere, come fa il calendario: qui non
            si legge nulla del giocatore e non parte nessuna chiamata —
            l'avvertenza in testa al file resta vera alla lettera, My
            Attendance NON conta nulla e non porta badge.
            La forma è quella delle altre voci vive: `<button>` con
            `onClick` e `navigate`, mai un `<Link>`; l'etichetta perde la
            coda « — coming soon »; l'icona è Font Awesome. */}
        <button
          type="button"
          className="qpsc-btn-gold"
          onClick={() => navigate("/attendance")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaClipboardCheck />
          </span>
          My Attendance
        </button>

        {/* IL BOTTONE AUSTRALIA GOAL DELLA SQUADRA (cantiere TEAM-LINK),
          DENTRO questo secondo `<nav>`, fra My Attendance e Clinic: è il
          link della SQUADRA che l'admin imposta, non una voce di rotta.
          Si mostra da sé solo se quella squadra ha un `link` — la guardia è
          tutta dentro il componente, come per EnrolNextSeason: qui non c'è
          nulla da decidere.
          ⚠️ FUORI DAL `<p>` della riga squadra: la riga è un flex
          `nowrap` che tiene insieme nome e pastiglia (un bottone dentro
          combatterebbe con la pastiglia per lo spazio).
          ⚠️ NESSUN MARGINE: il respiro §4 (16px) sopra e sotto lo porta già
          il `gap` di `.qpsc-menu` (HomeShell.css), come per le voci
          accanto. Dichiararlo lo raddoppierebbe a 32. */}
      <AustraliaGoalButton link={link} />

        {/* CLINIC / OFF SEASON / SPECIALS (23/9/2026): l'ingresso all'area
            clinic del socio, /clinics, uscito da More per decisione del
            committente. Etichetta e icona sono quelle del bottone admin
            (HomeAdmin.jsx). Voce INTERNA: `<button>` con `navigate`, come
            My Profile qui sotto — non la forma `<a>` dello Shop, che è un
            link esterno — e oro NORMALE (l'accento resta allo Shop). */}
        <button
          type="button"
          className="qpsc-btn-gold"
          onClick={() => navigate("/clinics")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaFutbol />
          </span>
          Clinic / Off Season / Specials
        </button>

        {/* Unica voce esterna, e unica variante d'accento della pagina
            (§5.1: al massimo una — My Profile qui sotto resta un
            pulsante oro NORMALE). L'indirizzo arriva da clubLinks. */}
        <a
          className="qpsc-btn-gold qpsc-btn-gold--accent"
          href={SHOP_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="qpsc-btn-gold__icon">
            <FaShoppingBag />
          </span>
          QPSC Online Shop
        </a>

        {/* Voce INTERNA: `<button>` con `navigate`, mai un `<Link>`
            (schema di HomeSuperadmin e di HomeParent). La destinazione è
            la stessa che le home vecchie davano a «My Profile»,
            /myprofile, con la stessa icona. Porta il doppio respiro
            (`.qpsc-player-spaced`), che cade fra lo Shop e lei e stacca
            la coda (lei e «More / Assistance»): le voci sorelle non si
            muovono. */}
        <button
          type="button"
          className="qpsc-btn-gold qpsc-player-spaced"
          onClick={() => navigate("/myprofile")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaUserCircle />
          </span>
          My Profile
        </button>
        <button
          type="button"
          className="qpsc-btn-gold"
          onClick={() => navigate("/more")}
        >
          <span className="qpsc-btn-gold__icon">
            <FaLifeRing />
          </span>
          More / Assistance
        </button>
      </nav>

      {/* BANNER DI CHIUSURA, in coda dopo il menu. La resa è del telaio,
          `.qpsc-adbanner` (HomeShell.css): una classe sola per tutte le
          home che lo mostrano. NON è cliccabile: nessun `<a>`, nessun
          `onClick`, nessun `role` — è un'immagine e basta, e sta FUORI dal
          `<nav>` proprio perché non è una voce di navigazione.
          L'immagine è servita da `public/`, quindi non si
          importa: il percorso è assoluto e l'URL è CODIFICATO, perché il
          file sul disco si chiama «footer AG.png», con uno spazio nel
          nome, e in un URL lo spazio è `%20`. */}
      <img
        className="qpsc-adbanner"
        src="/assets/footer%20AG.png"
        alt="AustraliaGoal"
      />
    </HomeShell>
  );
};

export default HomePlayer;
